import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LanguageIcon from '@mui/icons-material/Language';

import photoRaul     from '../assets/faculty/raul_gomez.png';
import photoOscar    from '../assets/faculty/oscar_rodriguez.png';
import photoRut      from '../assets/faculty/rut_gonzalez.png';
import photoJoseH    from '../assets/faculty/jose_serpas.png';
import photoJuanC    from '../assets/faculty/juan_alas.png';
import photoMaricela from '../assets/faculty/maricela_merino.png';
import photoGabriel  from '../assets/faculty/gabriel_duran.png';
import photoJuan from '../assets/faculty/Juan.jpeg';
import photoPalomo from '../assets/faculty/palomo.jpg';


const TEACHERS = [
  {
    id: 1,
    name: "Ing. Raul Gomez Pineda",
    subject: "Calculus I, Calculus II, General Physics",
    area: "business",
    description:
      "Electrical Engineer and MBA from UCA. Certified in E-Learning and virtual education platform management (2023), ChatGPT for Teachers (2024), and Applied Ethics in Higher Education (2025).",
    photo: photoRaul,
  },
  {
    id: 2,
    name: "Lic. Oscar Rodriguez Gonzalez",
    subject: "General Chemistry",
    area: "liberal",
    description:
      "PhD in Food Science from the University of Guelph, Canada. MSc in Biosystems and Agricultural Engineering from Texas A&M University. MBA in Operations Management from the Jack Welch Management Institute, Washington DC.",
    photo: photoOscar,
  },
  {
    id: 3,
    name: "Licda. Rut Gonzalez Mendez",
    subject: "Algebra, Intro to Financial Accounting, Financial Accounting",
    area: "business",
    description:
      "Bachelor's in Economics from UCA. Specialist in International Migration with a focus on financial and economic analysis.",
    photo: photoRut,
  },
  {
    id: 4,
    name: "Lic. Jose Hernan Serpas Comas",
    subject: "Political Philosophy, Project Management, Business Communication",
    area: "liberal",
    description:
      "Law degree from Universidad Católica Santa Teresa de Jesús de Ávila. Master's in Political Science from the Autonomous University of Barcelona. Certified Higher Education Instructor (2023), ChatGPT for Teachers (2024), and Diploma in Research Applied to Transparency and Anti-Corruption (2020).",
    photo: photoJoseH,
  },
  {
    id: 5,
    name: "Lic. Juan Carlos Alas",
    subject: "Business Management, Managerial Accounting",
    area: "business",
    description:
      "Bachelor's in Business Administration from UFG. Master's in Business Administration from UTEC. Experienced in corporate management and accounting applied to organizational decision-making.",
    photo: photoJuanC,
  },
  {
    id: 6,
    name: "Licda. Maricela Beraly Merino",
    subject: "Art History",
    area: "liberal",
    description:
      "Bachelor's in Visual Arts from the University of El Salvador. Dedicated to the study and teaching of art history and plastic arts within the context of higher education.",
    photo: photoMaricela,
  },
  {
    id: 7,
    name: "Lic. Gabriel Duran",
    subject: "General Psychology",
    area: "liberal",
    description:
      "Licensed Psychologist with a focus on general psychology and its application in educational and behavioral contexts at the higher education level.",
    photo: photoGabriel,
  },
  {
    id: 8,
    name: "Lic. Juan Jose Lopez Rogel",
    subject: "Macroeconomics",
    area: "business",
    description:
      "Bachelor's Degree in Economics – UCA, Master's in International Economics Master's, in Economic Analysis and Policy",
    photo: photoJuan,
  },
   {
    id: 9,
    name: "Ing. Carlos Eduardo Palomo Sosa",
    subject: "Human talent management, Business law, Information Technology",
    area: "business",
    description:
      "Mechanical Engineer. University of El Salvador. Master's Degree in Software Architecture.",
    photo: photoPalomo,
  },
];

const AREAS = [
  { value: 'all',      label: 'All',            icon: <SchoolIcon sx={{ fontSize: 18 }} /> },
  { value: 'business', label: 'Business Admin', icon: <BusinessCenterIcon sx={{ fontSize: 18 }} /> },
  { value: 'liberal',  label: 'Liberal Arts',   icon: <LanguageIcon sx={{ fontSize: 18 }} /> },
];

const AREA_COLORS = {
  business: '#2B2B6B',
  liberal:  '#E31E24',
};

const getInitials = (name) =>
  name
    .replace(/^(Prof\.|Ing\.|Lic\.|Licda\.|MSIE\.)\s*/i, '')
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('');

export default function FacultySection() {
  const [search, setSearch] = useState('');
  const [area, setArea]     = useState('all');

  const filtered = TEACHERS.filter((t) => {
    const matchArea   = area === 'all' || t.area === area;
    const matchSearch =
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.subject.toLowerCase().includes(search.toLowerCase());
    return matchArea && matchSearch;
  });

  return (
    <>
      <PageHeader
        title="Our Faculty"
        subtitle="Meet the professionals who will guide your academic journey at American College."
        breadcrumbs={[
          { label: 'Home',     href: '/' },
          { label: 'About Us', href: '/about-us' },
          { label: 'Faculty',  href: '/about-us/faculty' },
        ]}
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 5,
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
          }}
        >
          <TextField
            placeholder="Search by name or subject..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ maxWidth: { md: 300 }, width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#aaa' }} />
                </InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            value={area}
            exclusive
            onChange={(_, val) => val && setArea(val)}
            size="small"
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            {AREAS.map((a) => (
              <ToggleButton
                key={a.value}
                value={a.value}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  gap: 0.5,
                  border: '1px solid #E0E0E0 !important',
                  borderRadius: '8px !important',
                  px: 1.5,
                  '&.Mui-selected': {
                    backgroundColor:
                      a.value === 'all' ? '#2B2B6B' : AREA_COLORS[a.value],
                    color: 'white',
                    '&:hover': {
                      backgroundColor:
                        a.value === 'all' ? '#1a1a4a' : AREA_COLORS[a.value],
                    },
                  },
                }}
              >
                {a.icon}
                {a.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Counter */}
        <Typography sx={{ color: '#999', fontSize: '0.88rem', mb: 3 }}>
          {filtered.length}{' '}
          {filtered.length === 1 ? 'faculty member found' : 'faculty members found'}
        </Typography>

        {/* Card Grid */}
        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: '#bbb', fontSize: '1.1rem' }}>
              No faculty members found matching your criteria.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr',
              },
              gap: 3,
            }}
          >
            {filtered.map((teacher) => {
              const color = AREA_COLORS[teacher.area];
              return (
                <Card
                  key={teacher.id}
                  elevation={0}
                  sx={{
                    border: '1px solid #E0E0E0',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 16px 40px ${color}22`,
                      borderColor: color,
                    },
                  }}
                >
                  {/* Top Color Strip */}
                  <Box
                    sx={{
                      height: 6,
                      flexShrink: 0,
                      background: `linear-gradient(90deg, ${color}, ${color}88)`,
                    }}
                  />

                  <CardContent
                    sx={{
                      p: 3,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {/* Avatar */}
                    <Avatar
                      src={teacher.photo || undefined}
                      alt={teacher.name}
                      sx={{
                        width: 90,
                        height: 90,
                        backgroundColor: `${color}22`,
                        color: color,
                        fontWeight: 800,
                        fontSize: '1.4rem',
                        border: `3px solid ${color}44`,
                        objectFit: 'cover',
                        mb: 2,
                        flexShrink: 0,
                      }}
                    >
                      {!teacher.photo && getInitials(teacher.name)}
                    </Avatar>

                    {/* Name */}
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1a1a1a',
                        fontWeight: 700,
                        fontSize: '1rem',
                        mb: 0.5,
                        textAlign: 'center',
                      }}
                    >
                      {teacher.name}
                    </Typography>

                    {/* Subject */}
                    <Typography
                      sx={{
                        color: color,
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        mb: 2,
                        textAlign: 'center',
                        minHeight: 40,
                      }}
                    >
                      {teacher.subject}
                    </Typography>

                    {/* Divider */}
                    <Box
                      sx={{
                        width: 32,
                        height: 3,
                        background: color,
                        borderRadius: 2,
                        mb: 2,
                        flexShrink: 0,
                      }}
                    />

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666',
                        lineHeight: 1.7,
                        fontSize: '0.88rem',
                        flex: 1,
                      }}
                    >
                      {teacher.description}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </>
  );
}